import { Actor, Color, Engine, vec, Vector } from "excalibur";
import { GetPlayable } from "../constants";
import { CombinableComponent } from "../ecs/combinable";
import { HighlightComponent } from "../ecs/highlight";
import { ResourcePerTurnComponent } from "../ecs/resources";
import { TargetableComponent } from "../ecs/target";
import { TileComponent } from "../ecs/tile";
import { TowerAnimations } from "../resources";
import { MatchKind, Playable, PlayableId, TowerAnimationSet } from "../types";
import { actorName } from "../utils/actor";

export type SerializedBuilding = {
  tilePos: { x: number; y: number };
  playableId: PlayableId;
  name: string;
};

export class Building extends Actor {
  animations: TowerAnimationSet;
  portrait: string;
  playable: Playable;
  matchName: MatchKind;

  static restore(serial: SerializedBuilding): Building {
    return new Building({
      ...serial,
      playable: GetPlayable(serial.playableId),
      tilePos: vec(serial.tilePos.x, serial.tilePos.y),
    });
  }

  serialize(): SerializedBuilding {
    return {
      tilePos: {
        x: this.get(TileComponent).pos.x,
        y: this.get(TileComponent).pos.y,
      },
      playableId: this.playable.name,
      name: this.name,
    } as const;
  }

  constructor({
    tilePos,
    playable,
    name,
  }: {
    tilePos: Vector;
    playable: Playable;
    name?: string;
  }) {
    const matchName = playable.combines.ownMatchString as MatchKind;
    super({
      name: name ?? actorName(matchName),
      width: 32,
      height: 32,
      z: 990,
      pos: tilePos,
      // collisionType: CollisionType.Active, // Collision Type Active means this participates in collisions read more https://excaliburjs.com/docs/collisiontypes
    });
    this.playable = playable;
    this.animations = TowerAnimations[matchName];
    this.matchName = this.portrait = matchName;

    // TODO: Better system for solidity

    // TODO: When combining, keep damage?
    // All buildings are now considered solid
    this.addTag("solid");
    this.addComponent(
      new CombinableComponent(this.playable, () => this.kill(true)),
    );
    this.addComponent(
      new ResourcePerTurnComponent(
        this.playable.label,
        this.playable.resources,
        true,
      ),
    );
  }

  override kill(remove?: boolean) {
    if (remove) {
      return super.kill();
    }
    this.graphics.use("die");
    this.removeTag("solid");
    //this.animations.die.events.once("end", () => super.kill());
  }

  override onInitialize(_engine: Engine) {
    // TODO: Get from absorbAllDmgOnDeath from data
    const tileCenter = this.scene?.tileMaps[0].getTile(
      this.pos.x,
      this.pos.y,
    )?.center;
    if (tileCenter) {
      this.pos.setTo(tileCenter.x, tileCenter.y);
    } else {
      this.logger.error(this.name, "Missing scene/tile");
    }

    this.graphics.add("idle", this.animations.idle);
    this.graphics.add("die", this.animations.die);
    this.graphics.use("idle");

    this.addComponent(
      new HighlightComponent({
        color: new Color(0, 255, 255, 0.8), // Cyan highlight
        scale: 1.15,
        method: "both", // Use both tint and scale for highlight
      }),
    );

    this.addComponent(
      new TargetableComponent(this.id, this.name, this.portrait),
    );

    // Add resource production component

    this.addComponent(new TileComponent());
    console.log("Building init finished");
  }
}
